import { STATE_CODES } from 'constants/states';
import { isLegalAge } from 'utils/date';
import { validateDateOfBirth } from 'utils/validate-date-of-birth';
import * as Yup from 'yup';

const patientInformationFormSchema = Yup.object()
    .required()
    .shape({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        dateOfBirth: Yup.string()
            .required('Date of birth is required')
            .test('valid', 'Date of birth is not valid', validateDateOfBirth),
        sex: Yup.mixed<'F' | 'M'>().oneOf(['F', 'M']).required('Sex is required'),
        email: Yup.string()
            .required('Email is required')
            .matches(
                // Source: https://emailregex.com/
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                'This email address is not valid'
            ),
        phoneNumber: Yup.string()
            .required('Phone number is required')
            .transform((value: string | number | undefined) => {
                return typeof value === 'string' ? value.replaceAll(/\D/g, '') : value;
            })
            // https://en.wikipedia.org/wiki/North_American_Numbering_Plan#Modern_plan
            .matches(/^([2-9][0-9]{2})([2-9][0-9]{2})(\d{4})$/, 'Phone number is not valid'),
        phoneType: Yup.mixed<'Home' | 'Mobile'>().oneOf(['Home', 'Mobile']).required('Phone type is required'),
        address: Yup.string().required('Address is required'),
        addressCount: Yup.string(),
        city: Yup.string().required('City is required'),
        state: Yup.mixed<(typeof STATE_CODES)[number]>()
            .oneOf(STATE_CODES, 'Please use a valid two-letter US state abbreviation.')
            .required('State is required'),
        zip: Yup.string()
            .ensure()
            .required('ZIP is required')
            .length(5, 'ZIP must be exactly 5 characters')
            .matches(/\d{5}/, 'ZIP is required')
            .required(),
        guardianCheck: Yup.bool().test(
            'required',
            function validate(this: { parent: PatientInformationFormSchema }, value): boolean {
                const { dateOfBirth } = this.parent;
                return value || !dateOfBirth || isLegalAge(dateOfBirth);
            }
        ),
    });

interface PatientInformationFormSchemaValidated {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    sex: 'F' | 'M';
    email: string;
    phoneNumber: string;
    phoneType: 'Home' | 'Mobile';
    address: string;
    addressCount?: string;
    city: string;
    state: (typeof STATE_CODES)[number];
    zip: string;
    guardianCheck?: boolean;
    schedulingnote?: string;
}

type PatientInformationFormSchema = Partial<PatientInformationFormSchemaValidated>;

export { patientInformationFormSchema, validateDateOfBirth };
export type { PatientInformationFormSchemaValidated, PatientInformationFormSchema };
